/**
 * Assistenza Tray — il roster dello staff (ADR-0082 D4/D9).
 *
 * **«Il roster staff siamo noi»**: a differenza di quello partner, che il rivenditore si
 * gestisce da Central dopo che nominiamo il primo admin, questo non ha un gemello
 * autogestito. Ogni cappello si concede da qui, e l'audit registra chi l'ha concesso.
 *
 * La pagina è volutamente POVERA, e le assenze sono la decisione (D4):
 *
 * - **niente ruoli.** Una gerarchia di supporto — chi ha in carico un ticket, chi può
 *   scalare — è una proprietà della CODA, non della persona: messa qui diventerebbe un
 *   potere globale, buono ovunque, che è esattamente ciò che l'arco evita.
 * - **niente interruttore.** L'arco partner ha `enabled` perché sopra c'è una SOCIETÀ e un
 *   rapporto commerciale può chiudersi lasciando le persone dove sono. Sopra lo staff non
 *   c'è nessuna società: «non lavora più qui» È togliere l'arco.
 * - **niente potere sulla flotta.** Chi indossa questo cappello legge e risponde ai ticket;
 *   non conia licenze e non provisiona nulla. Il confine non è affidato a questa pagina —
 *   lo tiene `tests/test_staff_boundary.py`, che cammina l'app vera.
 *
 * Il cartello a schermo dice l'ultima riga, perché «aggiungi allo staff» si legge
 * facilmente come «dai i poteri», ed è l'equivoco che il modello esiste per evitare.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LifeBuoy, Link2, MailWarning, UserMinus, UserPlus } from "lucide-react";
import * as React from "react";

import { ClaimLink } from "@/components/platform/ClaimLink";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  PlatformApiError,
  type StaffMember,
  type StaffMemberAdded,
  platformApi,
} from "@/lib/platform";

const STAFF_KEY = ["platform", "staff"];

function errorText(e: unknown, fallback = "Operazione non riuscita."): string {
  return e instanceof PlatformApiError ? e.message : fallback;
}

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

/**
 * Dare il cappello dell'assistenza a una persona.
 *
 * Nessun campo password, come per il partner: non teniamo mai una credenziale per conto di
 * qualcun altro. Se l'Account Tray esiste lo LEGHIAMO, altrimenti lo invitiamo e la
 * password se la sceglie lui. L'esito è una schermata e non un toast perché porta il
 * `claim_url`: il link è la garanzia, la mail è la comodità.
 */
function AddStaffDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [email, setEmail] = React.useState("");
  const [fullName, setFullName] = React.useState("");
  const [done, setDone] = React.useState<StaffMemberAdded | null>(null);

  const save = useMutation({
    mutationFn: () =>
      platformApi.addStaffMember({
        email: email.trim().toLowerCase(),
        full_name: fullName.trim() || null,
      }),
    onSuccess: (res) => {
      setDone(res);
      onSaved();
    },
  });

  if (done) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {done.mode === "linked" ? "Cappello assegnato" : "Invito inviato"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <p className="text-sm text-foreground">
              {done.mode === "linked" ? (
                <>
                  <strong>{done.member.email}</strong> aveva già un Account Tray: ora indossa anche
                  il cappello dell'assistenza. Entra da Tray Central con le credenziali che ha già.
                </>
              ) : (
                <>
                  Abbiamo creato l'Account Tray di <strong>{done.member.email}</strong>. La password
                  la sceglie lui raccogliendo l'invito — noi non ne teniamo una.
                </>
              )}
            </p>
            {!done.mail_sent && (
              <p className="text-sm text-warning">
                La mail non è partita. Passa tu il link qui sotto: senza, l'invito non arriva a
                nessuno.
              </p>
            )}
            {(done.claim_url || done.login_url) && (
              <ClaimLink url={(done.claim_url ?? done.login_url) as string} />
            )}
          </DialogBody>
          <DialogFooter>
            <Button onClick={onClose}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Aggiungi all'assistenza</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Entra in Tray Central con il suo Account Tray e vede la CODA dei ticket. Non c'è un
            ruolo da scegliere: la gerarchia sta nella coda, non nel cappello.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="sm-email">Email</Label>
            <Input
              id="sm-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="persona@tray.it"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sm-name">Nome e cognome (opzionale)</Label>
            <Input id="sm-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          {save.isError && <p className="text-sm text-danger">{errorText(save.error)}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || email.trim().length < 3}
          >
            Aggiungi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * L'esito di "Rigenera link" — e la seconda risposta è quella che conta.
 *
 * Se la persona ha già una password il backend NON conia niente: un claim link è un reset
 * password, e con esso si prenderebbe possesso di un Account Tray vivo, con i workspace che
 * quell'account raggiunge — che con l'assistenza non c'entrano.
 */
function ReissuedLinkDialog({
  outcome,
  onClose,
}: {
  outcome: StaffMemberAdded;
  onClose: () => void;
}) {
  const linked = outcome.mode === "linked";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{linked ? "Nessun link da rigenerare" : "Nuovo link d'accesso"}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {linked ? (
            <p className="text-sm text-foreground">
              <strong>{outcome.member.email}</strong> ha già una password: accede con quella. Se
              l'ha persa, la reimposta lei dalla pagina di accesso — un link generato da qui sarebbe
              un reset password, e non spetta a questa console.
            </p>
          ) : (
            <>
              <p className="text-sm text-foreground">
                Passa questo link a <strong>{outcome.member.email}</strong>: sceglierà la password e
                da quel momento entra in Tray Central. Scade tra 7 giorni.
              </p>
              {outcome.claim_url && <ClaimLink url={outcome.claim_url} />}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PlatformStaffPage() {
  const qc = useQueryClient();
  const [adding, setAdding] = React.useState(false);
  const [reissued, setReissued] = React.useState<StaffMemberAdded | null>(null);

  const staffQ = useQuery({ queryKey: STAFF_KEY, queryFn: () => platformApi.listStaff() });
  const invalidate = () => void qc.invalidateQueries({ queryKey: STAFF_KEY });

  const reissue = useMutation({
    mutationFn: (id: string) => platformApi.reissueStaffInvite(id),
    onSuccess: (res) => {
      setReissued(res);
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => platformApi.removeStaffMember(id),
    onSuccess: invalidate,
  });

  const rows = staffQ.data ?? [];

  return (
    <PageContainer>
      <PageHeader
        title="Assistenza Tray"
        description="Chi di noi risponde ai ticket dei clienti e dei rivenditori."
        actions={
          <Button onClick={() => setAdding(true)}>
            <UserPlus className="h-4 w-4" strokeWidth={1.5} />
            Aggiungi
          </Button>
        }
      />
      <PageContent>
        <p className="mb-4 max-w-3xl text-sm text-muted-foreground">
          Questo cappello dà accesso alla <strong>coda dei ticket</strong> su Tray Central, e a
          nient'altro: <strong>non conia licenze e non provisiona workspace</strong>. Non ha un
          ruolo da scegliere — chi ha in carico un ticket lo dice il ticket — e non ha un
          interruttore: chi non fa più assistenza, non ha più l'arco.
        </p>

        {rows.length === 0 && !staffQ.isLoading ? (
          <EmptyState
            icon={LifeBuoy}
            title="Nessuno in assistenza"
            description="Finché nessuno indossa questo cappello, la coda dei ticket su Central non ha chi la legge."
          />
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>Persona</Th>
                <Th>Stato</Th>
                <Th>Nel roster da</Th>
                <Th className="text-right">Azioni</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((m) => (
                <StaffRow
                  key={m.id}
                  member={m}
                  busy={reissue.isPending || remove.isPending}
                  onReissue={() => reissue.mutate(m.id)}
                  onRemove={() => {
                    if (
                      window.confirm(
                        `Togliere il cappello dell'assistenza a ${m.email}?\n\nL'Account Tray resta: perde solo l'accesso alla coda dei ticket.`,
                      )
                    ) {
                      remove.mutate(m.id);
                    }
                  }}
                />
              ))}
            </Tbody>
          </Table>
        )}

        {(reissue.isError || remove.isError) && (
          <p className="mt-3 text-sm text-danger">{errorText(reissue.error ?? remove.error)}</p>
        )}
      </PageContent>

      {adding && <AddStaffDialog onClose={() => setAdding(false)} onSaved={invalidate} />}
      {reissued && <ReissuedLinkDialog outcome={reissued} onClose={() => setReissued(null)} />}
    </PageContainer>
  );
}

/**
 * Una riga = un arco.
 *
 * "Rigenera link" compare SOLO su un invito ancora in volo (`claimed === false`): il backend
 * rifiuta di coniarne uno per chi ha già una password, quindi mostrarlo altrove
 * prometterebbe una cosa che non può succedere.
 */
function StaffRow({
  member,
  busy,
  onReissue,
  onRemove,
}: {
  member: StaffMember;
  busy: boolean;
  onReissue: () => void;
  onRemove: () => void;
}) {
  return (
    <Tr>
      <Td>
        <div className="font-medium text-foreground">{member.full_name || member.email}</div>
        {member.full_name && <div className="text-xs text-muted-foreground">{member.email}</div>}
      </Td>
      <Td>
        <div className="flex flex-wrap items-center gap-1.5">
          {member.state !== "active" && <Badge tone="danger">Account {member.state}</Badge>}
          {!member.claimed && (
            <Badge tone="warning" className="gap-1">
              <Link2 className="h-3 w-3" strokeWidth={1.5} />
              Invito da raccogliere
            </Badge>
          )}
          {member.claimed && !member.email_verified && (
            <Badge tone="warning" className="gap-1">
              <MailWarning className="h-3 w-3" strokeWidth={1.5} />
              Email non verificata
            </Badge>
          )}
          {member.claimed && member.email_verified && member.state === "active" && (
            <Badge tone="active">Attivo</Badge>
          )}
        </div>
      </Td>
      <Td className="text-muted-foreground">{fmtDate(member.created_at)}</Td>
      <Td className="text-right">
        <div className="flex justify-end gap-1">
          {!member.claimed && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={onReissue}>
              Rigenera link
            </Button>
          )}
          <Button size="sm" variant="ghost" disabled={busy} onClick={onRemove}>
            <UserMinus className="h-4 w-4" strokeWidth={1.5} />
            Togli
          </Button>
        </div>
      </Td>
    </Tr>
  );
}
